/**
 * Where a notification goes when you click it.
 *
 * The backend stores `entity_type` / `entity_id` rather than a URL, so the
 * route lives here — the API has no business knowing the frontend's paths, and
 * a stored URL would rot the moment a route is renamed.
 *
 * Not every entity has a detail page. `pre_lead` in particular only has list
 * views, so it falls back to the list rather than producing a dead link that
 * renders a blank screen. Anything unmapped returns null and the item renders
 * as plain text, which is the honest outcome — better than navigating somewhere
 * arbitrary and leaving the reader to work out why.
 */

/**
 * The invoice page loads by deal, not by invoice — /builder/accounts/invoice/:id
 * is the deal's invoice list. Invoice notifications carry the invoice as their
 * entity, so opening entity_id landed on an empty "No Data Found" page. The
 * backend puts the deal on the payload for exactly this; use it when present.
 */
function invoiceRoute(id, notification) {
  const dealId = Number(notification?.payload?.dealId);
  return `/builder/accounts/invoice/${Number.isInteger(dealId) && dealId > 0 ? dealId : id}`;
}

const DETAIL_ROUTES = {
  lead: (id) => `/builder/sales/${id}`,
  deal: (id) => `/builder/deals/${id}`,
  invoice: invoiceRoute,
  live_task: (id) => `/builder/operations/managementTasks/${id}`,
  daily_requirement: (id) => `/builder/inventory/requirements/${id}`,
  employee: (id) => `/builder/hr/employees/${id}`,
  project: (id) => `/builder/management/company/projects/${id}`,
  order: (id) => `/builder/inventory/orders/requirement/${id}`
};

/** Entities with no detail page — sent to the nearest list instead. */
const LIST_FALLBACKS = {
  pre_lead: '/builder/sales/prelead-lists/all-leads',
  payment_stage: '/builder/accounts/invoice',
  return_material: '/builder/inventory/requirements',
  // The route is singular (`/builder/hr/leave`), not `/builder/hr/leaves`.
  leave_request: '/builder/hr/leave',
  payroll_period: '/builder/hr/payroll',
  rfq: '/builder/inventory/requirements',
  quotation: '/builder/inventory/requirements',
  company_policy_version: '/builder/hr/vault',
  employee_document: '/builder/hr/vault'
};

/**
 * Where a notification with no entity goes.
 *
 * Digests and roll-ups — "you have 4 overdue follow-ups", "3 live tasks are
 * past the planned end date" — summarise a set, not a record, so the backend
 * stores no entity_type for them and the resolver returned null.
 *
 * These are functions rather than plain strings because a destination is not
 * always reachable as a bare path: `/builder/priority/leads` gates its query on
 * `enabled: !!projectID` and has no project picker of its own, so arriving
 * without one renders an empty table and a "Follow Up null" heading forever.
 * The notification already carries the project it is about; passing it is what
 * makes the link land on something.
 */

/**
 * Follow-up digests.
 *
 * Project-scoped ones (the management roll-up) can open the list directly.
 * The per-salesperson reminders span every project that person works on, so
 * there is no single projectID to pass — and sending them to a page that
 * cannot load without one would be worse than not linking at all. They go to
 * the sales dashboard, which has its own project filter.
 */
function followUpsLink(notification) {
  const projectId = notification?.project_id;
  if (!projectId) return '/builder/sales';
  return `/builder/priority/leads?projectID=${encodeURIComponent(projectId)}`;
}

const EVENT_ROUTES = {
  'followup.due_today': followUpsLink,
  'followup.overdue': followUpsLink,
  'followup.overdue.rollup': followUpsLink,
  // These two pages default to the first project when none is given, so a bare
  // path is enough — verified against their own project fallbacks.
  'task.delayed.rollup': () => '/builder/operations/tasks',
  'ops.priority_tasks_today': () => '/builder/operations/tasks',
  'inventory.priority_tasks_today': () => '/builder/inventory/dailytasks'
};

export function notificationLink(notification) {
  const type = notification?.entity_type;
  const id = notification?.entity_id;

  if (type) {
    if (id && DETAIL_ROUTES[type]) return DETAIL_ROUTES[type](id, notification);
    if (LIST_FALLBACKS[type]) return LIST_FALLBACKS[type];
  }

  // Falls through for digests, which carry no entity at all, and for an entity
  // type nothing maps yet — both are better served by the list than by nothing.
  const build = EVENT_ROUTES[notification?.event_type];
  return build ? build(notification) : null;
}

/**
 * Relative time, short enough for a dropdown row.
 *
 * Deliberately not a library: this is the only place the app needs it, and
 * "3h" reads better in a 320px dropdown than "about 3 hours ago".
 */
export function relativeTime(value) {
  if (!value) return '';
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return '';

  const seconds = Math.floor((Date.now() - then) / 1000);
  // Clock skew between the server and the browser can put a fresh notification
  // slightly in the future; "now" beats "-2s".
  if (seconds < 60) return 'now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  return new Date(then).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short'
  });
}

/** Tailwind classes for the priority dot. High and above earn a colour. */
export function priorityDotClass(priority) {
  if (priority === 'critical') return 'bg-red-500';
  if (priority === 'high') return 'bg-amber-500';
  return 'bg-transparent';
}
